
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import type { UserInspiration } from '@/types/quiz'

export const useUserInspirations = () => {
  const [inspirations, setInspirations] = useState<UserInspiration[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const fetchInspirations = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        console.log('No session found, clearing inspirations')
        setInspirations([])
        setLoading(false)
        return
      }

      console.log('Fetching inspirations for user:', session.user.id)

      const { data, error } = await supabase
        .from('user_inspirations')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('added_to_my_list', false)
        .order('created_at', { ascending: false })

      if (error) throw error
      
      console.log('Raw inspirations from database:', data)
      
      // Additional filtering to ensure we only get valid inspirations
      const validInspirations = (data || []).filter(inspiration => {
        const isValid = inspiration.user_id === session.user.id && !inspiration.added_to_my_list
        if (!isValid) {
          console.log('Filtering out invalid inspiration:', inspiration)
        }
        return isValid
      })
      
      console.log('Valid inspirations after filtering:', validInspirations)
      setInspirations(validInspirations)
    } catch (error: any) {
      console.error('Error fetching inspirations:', error)
      toast({
        title: "Error",
        description: "Failed to load inspirations",
        variant: "destructive",
      })
      setInspirations([]) // Set empty array on error
    } finally {
      setLoading(false)
    }
  }, [toast])

  const addToMyList = useCallback(async (inspirationId: string) => {
    try {
      const { error } = await supabase
        .from('user_inspirations')
        .update({ added_to_my_list: true, updated_at: new Date().toISOString() })
        .eq('id', inspirationId)

      if (error) throw error

      // Immediately remove from local state for instant UI update
      setInspirations(prev => prev.filter(item => item.id !== inspirationId))

      toast({
        title: "Added to your list",
        description: "Item has been added to your tasks or resources",
      })

      return true
    } catch (error: any) {
      console.error('Error adding to my list:', error)
      toast({
        title: "Error",
        description: "Failed to add item to your list",
        variant: "destructive",
      })
      return false
    }
  }, [toast])

  const processQuizRecommendations = useCallback(async (quizId: string, userAnswers: number[]) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      console.log('Processing quiz recommendations for quiz:', quizId, 'answers:', userAnswers)

      // Fetch quiz recommendations
      const { data: recommendations, error } = await supabase
        .from('quiz_recommendations')
        .select('*')
        .eq('quiz_id', quizId)

      if (error) throw error

      const newInspirations: any[] = []

      recommendations?.forEach(rec => {
        const mapping = rec.question_answer_mapping as Record<string, number[]>
        if (!mapping) return

        // Check if user's answers match any criteria
        const shouldRecommend = Object.entries(mapping).some(([questionKey, answerIndices]) => {
          const questionIndex = parseInt(questionKey.replace('question_', '')) - 1
          return answerIndices.includes(userAnswers[questionIndex])
        })

        if (shouldRecommend) {
          newInspirations.push({
            user_id: session.user.id,
            resource_id: rec.resource_id,
            task_id: rec.task_id,
            source_quiz_id: quizId,
            added_to_my_list: false
          })
        }
      })

      console.log('New inspirations to be created:', newInspirations)

      if (newInspirations.length > 0) {
        const { error: insertError } = await supabase
          .from('user_inspirations')
          .insert(newInspirations)

        if (insertError) throw insertError

        toast({
          title: "New recommendations available!",
          description: `We've added ${newInspirations.length} personalized recommendations for you`,
        })

        // Refresh inspirations
        fetchInspirations()
      }
    } catch (error: any) {
      console.error('Error processing quiz recommendations:', error)
    }
  }, [toast, fetchInspirations])

  useEffect(() => {
    fetchInspirations()

    // Set up real-time subscription
    const channel = supabase
      .channel('user-inspirations-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_inspirations'
        },
        (payload) => {
          console.log('Real-time inspiration change:', payload)
          fetchInspirations()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [fetchInspirations])

  return {
    inspirations,
    loading,
    addToMyList,
    processQuizRecommendations,
    refreshInspirations: fetchInspirations
  }
}
